'use client'

import Link from 'next/link'
import React, { Fragment } from 'react'

import { Media } from '@/components/Media'
import { cn } from '@/utilities/ui'
import useClickableCard from '@/utilities/useClickableCard'

import type { Article } from '@/payload-types'

export type CardArticleData = Pick<Article, 'slug' | 'meta' | 'title' | 'topics'>

export const Card: React.FC<{
  alignItems?: 'center'
  className?: string
  doc?: CardArticleData
  relationTo?: 'articles'
  showTopics?: boolean
  title?: string
}> = (props) => {
  const { cardRef, linkRef } = useClickableCard({})
  const { className, doc, relationTo, showTopics, title: titleFromProps } = props

  const { slug, topics, meta, title } = doc || {}
  const { description, image: metaImage } = meta || {}

  const hasTopics = topics && Array.isArray(topics) && topics.length > 0
  const titleToUse = titleFromProps || title
  const sanitizedDescription = description?.replace(/\s/g, ' ') // replace non-breaking space with white space
  const href = relationTo === 'articles' ? `/learn/articles/${slug}` : `/${slug}`

  return (
    <article
      className={cn(
        'overflow-hidden rounded-lg border border-border bg-card hover:cursor-pointer',
        className,
      )}
      ref={cardRef}
    >
      <div className="relative w-full">
        {!metaImage && <div className="">No image</div>}
        {metaImage && typeof metaImage !== 'string' && (
          <Media resource={metaImage} context={'card'} />
        )}
      </div>
      <div className="p-4">
        {showTopics && hasTopics && (
          <div className="mb-4 text-sm uppercase">
            <div>
              {topics?.map((topic, index) => {
                if (typeof topic === 'object') {
                  const { title: titleFromTopic } = topic

                  const topicTitle = titleFromTopic || 'Untitled topic'

                  const isLast = index === topics.length - 1

                  return (
                    <Fragment key={index}>
                      {topicTitle}
                      {!isLast && <Fragment>, &nbsp;</Fragment>}
                    </Fragment>
                  )
                }

                return null
              })}
            </div>
          </div>
        )}
        {titleToUse && (
          <div className="prose">
            <h3>
              <Link className="not-prose" href={href} ref={linkRef}>
                {titleToUse}
              </Link>
            </h3>
          </div>
        )}
        {description && <div className="mt-2">{description && <p>{sanitizedDescription}</p>}</div>}
      </div>
    </article>
  )
}
